// oxlint-disable max-lines-per-function, complexity
import { merge } from 'lodash-es';
import { assertEvent, enqueueActions } from 'xstate';

import { machine } from './actor';

/**
 * Provides the Variant A machine's action and guard implementations, referenced by the state
 * chart in `./actor`. Kept separate from the chart so that external dependencies can be injected
 * into the implementation here.
 */
export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const updatedDraft = merge({}, context.draft, event.draft);

				// update the child machine's context
				enqueue.assign({ draft: updatedDraft });

				// sync the updated child machine's context to the parent machine's context
				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			onError: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'error');

				const currentDraft = context.draft;

				const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
				const description = `${issuePrompt} <br /><br />${personalStory}`;

				const userDraft = {
					type: 'user_draft',
					petitionDraft: { ask: null, description, image: null },
					recommendedDraft: null,
					editingDraft: null,
					metadata: { imageJobId: null },
				};

				const updatedDraft = merge({}, context.draft, userDraft);

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			unsetDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'back');

				const targetBranch = event.target; // target = null

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, context.draft, { type: null });

					return context.draft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
			persistDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const baseDraft = merge({}, context.draft, event.draft);

				const targetBranch = event.target; // 'ai' | 'user'

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, baseDraft, { type: null });
					if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'ai_draft' });
					if (targetBranch === 'user') return merge({}, baseDraft, { type: 'user_draft' });
					return baseDraft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
		},
		guards: {
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');

				return event.target === params.target;
			},
			// -- branching and transition point
			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},
			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},
		},
	});
